import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import {
  FiMapPin,
  FiCheck,
  FiX,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiShield,
  FiUser,
  FiMail,
  FiCalendar,
  FiClock
} from 'react-icons/fi';

const GpsAccessControl = () => {
  const { user } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [updatingAccess, setUpdatingAccess] = useState({});

  useEffect(() => {
    fetchAgencies();
  }, []);

  const fetchAgencies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/users');
      setAgencies(response.data.data || []);
    } catch (error) {
      console.error('Error fetching agencies:', error);
      toast.error('Erreur lors de la récupération des agences');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAccess = async (agencyId) => {
    try {
      setUpdatingAccess(prev => ({ ...prev, [agencyId]: true }));
      
      const response = await api.post(`/api/users/${agencyId}/grant-gps-access`);
      
      if (response.data.success) {
        toast.success(`Accès GPS accordé à ${response.data.data.grantedBy}`);
        fetchAgencies(); // Refresh the list
      }
    } catch (error) {
      console.error('Error granting GPS access:', error);
      toast.error('Erreur lors de l\'octroi de l\'accès GPS');
    } finally {
      setUpdatingAccess(prev => ({ ...prev, [agencyId]: false }));
    }
  };

  const handleRevokeAccess = async (agencyId) => {
    try {
      setUpdatingAccess(prev => ({ ...prev, [agencyId]: true }));
      
      const response = await api.post(`/api/users/${agencyId}/revoke-gps-access`);
      
      if (response.data.success) {
        toast.success(`Accès GPS révoqué par ${response.data.data.revokedBy}`);
        fetchAgencies(); // Refresh the list
      }
    } catch (error) {
      console.error('Error revoking GPS access:', error);
      toast.error('Erreur lors de la révocation de l\'accès GPS');
    } finally {
      setUpdatingAccess(prev => ({ ...prev, [agencyId]: false }));
    }
  };

  const filteredAgencies = agencies.filter(agency => {
    const matchesSearch = agency.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agency.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agency.company?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' ||
                         (filterStatus === 'with-access' && agency.canAccessGpsTracking) ||
                         (filterStatus === 'without-access' && !agency.canAccessGpsTracking);
    
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (agency) => {
    if (agency.canAccessGpsTracking) {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full flex items-center gap-1">
          <FiCheck className="w-3 h-3" />
          Accès accordé
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full flex items-center gap-1">
          <FiX className="w-3 h-3" />
          Accès refusé
        </span>
      );
    }
  };

  const getAccessInfo = (agency) => {
    if (!agency.canAccessGpsTracking) {
      return (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Aucun accès GPS accordé
        </div>
      );
    }

    return (
      <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
        {agency.gpsTrackingAccessGrantedAt && (
          <div className="flex items-center gap-1">
            <FiCalendar className="w-3 h-3" />
            Accordé le: {new Date(agency.gpsTrackingAccessGrantedAt).toLocaleDateString('fr-FR')}
          </div>
        )}
        {agency.gpsTrackingAccessGrantedBy && (
          <div className="flex items-center gap-1">
            <FiUser className="w-3 h-3" />
            Par: {agency.gpsTrackingAccessGrantedBy.name || agency.gpsTrackingAccessGrantedBy.email}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Contrôle d'Accès GPS
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gérez l'accès au suivi GPS pour toutes les agences
          </p>
        </div>
        <button
          onClick={fetchAgencies}
          className="btn btn-secondary flex items-center gap-2"
        >
          <FiRefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
              <FiMapPin className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Agences</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{agencies.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
              <FiCheck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avec Accès GPS</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {agencies.filter(a => a.canAccessGpsTracking).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <FiX className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Sans Accès GPS</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {agencies.filter(a => !a.canAccessGpsTracking).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher une agence..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input"
            >
              <option value="all">Tous les statuts</option>
              <option value="with-access">Avec accès GPS</option>
              <option value="without-access">Sans accès GPS</option>
            </select>
          </div>
        </div>
      </div>

      {/* Agencies List */}
      <div className="card p-6">
        <div className="space-y-4">
          {filteredAgencies.map((agency) => (
            <div
              key={agency._id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                      <FiUser className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {agency.name || 'Nom non spécifié'}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {agency.company || 'Entreprise non spécifiée'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                    <div className="flex items-center gap-1">
                      <FiMail className="w-3 h-3" />
                      {agency.email}
                    </div>
                    <div className="flex items-center gap-1">
                      <FiShield className="w-3 h-3" />
                      {agency.role}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 mb-3">
                    {getStatusBadge(agency)}
                    {getAccessInfo(agency)}
                  </div>
                </div>
                
                <div className="flex gap-2 ml-4">
                  {agency.canAccessGpsTracking ? (
                    <button
                      onClick={() => handleRevokeAccess(agency._id)}
                      disabled={updatingAccess[agency._id]}
                      className="btn btn-danger btn-sm flex items-center gap-2"
                      title="Révoquer l'accès GPS"
                    >
                      <FiX className="w-4 h-4" />
                      Révoquer
                    </button>
                  ) : (
                    <button
                      onClick={() => handleGrantAccess(agency._id)}
                      disabled={updatingAccess[agency._id]}
                      className="btn btn-success btn-sm flex items-center gap-2"
                      title="Accorder l'accès GPS"
                    >
                      <FiCheck className="w-4 h-4" />
                      Accorder
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {filteredAgencies.length === 0 && (
          <div className="text-center py-12">
            <FiMapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm || filterStatus !== 'all' 
                ? 'Aucune agence trouvée avec ces critères'
                : 'Aucune agence trouvée'
              }
            </p>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="card p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <FiShield className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-2">
              Instructions de Gestion
            </h3>
            <div className="text-sm text-blue-800 dark:text-blue-200 space-y-2">
              <p>
                • <strong>Accorder l'accès GPS :</strong> Permet à l'agence d'utiliser la page "Suivi GPS" 
                pour localiser et suivre ses véhicules en temps réel.
              </p>
              <p>
                • <strong>Révoquer l'accès GPS :</strong> Supprime l'accès au suivi GPS. 
                L'agence verra un message d'accès refusé sur la page GPS.
              </p>
              <p>
                • <strong>Traçabilité :</strong> Toutes les actions sont enregistrées avec la date 
                et l'identité du superadmin qui les a effectuées.
              </p>
              <p>
                • <strong>Sécurité :</strong> Seuls les superadmins peuvent gérer les accès GPS. 
                Les agences ne peuvent pas modifier leurs propres permissions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GpsAccessControl;
